import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Box,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  PlayArrow as RunIcon,
  Pause as PauseIcon,
  Edit as EditIcon,
  Visibility as LogsIcon,
  AccountBalance as AccountingIcon
} from '@mui/icons-material';
import axios from 'axios';
import { cronToHumanReadable } from '../../utils/cronUtils';

export default function AccountingPipelinesSection({ onNotification }) {
  const [pipelines, setPipelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPipeline, setEditingPipeline] = useState(null);
  const [editForm, setEditForm] = useState({
    cron_expression: '',
    sync_window_days: 1
  });

  // Custom run dialog state
  const [runDialogOpen, setRunDialogOpen] = useState(false);
  const [runningPipeline, setRunningPipeline] = useState(null);
  const [runLimit, setRunLimit] = useState(1000);

  // Logs dialog state
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [selectedJobLogs, setSelectedJobLogs] = useState(null);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // Load accounting pipelines
  const loadPipelines = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/accounting/pipelines');
      setPipelines(response.data.pipelines || []);
      setError(null);
    } catch (err) {
      console.error('Error loading accounting pipelines:', err);
      setError('Failed to load accounting pipelines');
      if (onNotification) {
        onNotification('Failed to load accounting pipelines', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPipelines();

    // Auto-refresh every 60 seconds
    const interval = setInterval(loadPipelines, 60000);
    return () => clearInterval(interval);
  }, []);

  // Toggle pipeline enabled/disabled
  const handleTogglePipeline = async (pipelineName, currentStatus) => {
    try {
      await axios.post(`/api/accounting/pipelines/${pipelineName}/toggle`, {
        is_enabled: !currentStatus
      });

      if (onNotification) {
        onNotification(
          `Accounting pipeline ${currentStatus ? 'disabled' : 'enabled'} successfully`,
          'success'
        );
      }

      loadPipelines();
    } catch (err) {
      console.error('Error toggling accounting pipeline:', err);
      if (onNotification) {
        onNotification('Failed to toggle accounting pipeline', 'error');
      }
    }
  };

  // Manual trigger pipeline (quick run with default limit)
  const handleRunPipeline = async (pipelineName) => {
    try {
      const response = await axios.post(`/api/accounting/pipelines/${pipelineName}/trigger`, {
        limit: 1000
      });

      if (onNotification) {
        onNotification(
          `Accounting pipeline queued successfully! Job ID: ${response.data.jobId}`,
          'success'
        );
      }
    } catch (err) {
      console.error('Error triggering accounting pipeline:', err);
      if (onNotification) {
        onNotification('Failed to trigger accounting pipeline', 'error');
      }
    }
  };

  // Open edit dialog
  const handleEditPipeline = (pipeline) => {
    setEditingPipeline(pipeline);
    setEditForm({
      cron_expression: pipeline.cron_expression || '0 2,14 * * *',
      sync_window_days: pipeline.sync_window_days || 1
    });
    setEditDialogOpen(true);
  };

  // Save pipeline edits
  const handleSaveEdit = async () => {
    try {
      await axios.post(
        `/api/accounting/pipelines/${editingPipeline.pipeline_name}/update`,
        editForm
      );

      if (onNotification) {
        onNotification('Accounting pipeline updated successfully', 'success');
      }

      setEditDialogOpen(false);
      loadPipelines();
    } catch (err) {
      console.error('Error updating accounting pipeline:', err);
      if (onNotification) {
        onNotification('Failed to update accounting pipeline', 'error');
      }
    }
  };

  // Open custom run dialog
  const handleOpenCustomRun = (pipeline) => {
    setRunningPipeline(pipeline);
    setRunLimit(1000);
    setRunDialogOpen(true);
  };

  // Run pipeline with custom limit
  const handleCustomRun = async () => {
    try {
      const response = await axios.post(
        `/api/accounting/pipelines/${runningPipeline.pipeline_name}/trigger`,
        { limit: runLimit }
      );

      if (onNotification) {
        onNotification(
          `Accounting pipeline queued with limit ${runLimit}! Job ID: ${response.data.jobId}`,
          'success'
        );
      }

      setRunDialogOpen(false);
    } catch (err) {
      console.error('Error running custom accounting pipeline:', err);
      if (onNotification) {
        onNotification('Failed to run accounting pipeline', 'error');
      }
    }
  };

  // View job logs
  const handleViewLogs = async (pipelineName) => {
    setLoadingLogs(true);
    setLogsDialogOpen(true);

    try {
      // Get recent pipeline jobs
      const response = await axios.get(
        `/api/accounting/pipelines/${pipelineName}/jobs?limit=20`
      );
      setSelectedJobLogs(response.data.jobs || []);
    } catch (err) {
      console.error('Error loading accounting logs:', err);
      setSelectedJobLogs([]);
      if (onNotification) {
        onNotification('Failed to load accounting job logs', 'error');
      }
    } finally {
      setLoadingLogs(false);
    }
  };

  // Format relative time
  const formatRelativeTime = (timestamp) => {
    if (!timestamp || timestamp === '1970-01-01 00:00:00') return 'Never';

    const now = new Date();
    const time = new Date(timestamp.replace(' ', 'T') + 'Z');
    const diffMs = now - time;
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  // Format next run time
  const formatNextRun = (timestamp) => {
    if (!timestamp) return 'Not scheduled';

    const time = new Date(timestamp.replace(' ', 'T') + 'Z');
    const now = new Date();

    if (time <= now) return 'Due now';

    const diffMs = time - now;
    const diffMins = Math.ceil(diffMs / (1000 * 60));

    if (diffMins < 60) return `In ${diffMins}m`;
    const diffHours = Math.ceil(diffMins / 60);
    if (diffHours < 24) return `In ${diffHours}h`;

    return time.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get pipeline display name
  const getPipelineDisplayName = (name) => {
    const displayNames = {
      'accounting_journal_entries': 'Journal Entry Creator',
      'accounting_reconciliation_matcher': 'Reconciliation Matcher'
    };
    return displayNames[name] || name;
  };

  // Get pipeline description
  const getPipelineDescription = (name) => {
    const descriptions = {
      'accounting_journal_entries': 'Processes bank payout and PayPal order matches to create double-entry journal entries',
      'accounting_reconciliation_matcher': 'Runs reconciliation scripts to match transactions and populate match tables'
    };
    return descriptions[name] || '';
  };

  if (loading) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading accounting pipelines...
        </Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  if (pipelines.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        No accounting pipelines configured.
      </Alert>
    );
  }

  return (
    <Paper sx={{ mt: 4 }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountingIcon color="primary" />
          <Typography variant="h6" fontWeight="600">
            Accounting Automation Pipelines
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Automated journal entry creation from reconciled transactions
        </Typography>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Pipeline</TableCell>
              <TableCell>Schedule</TableCell>
              <TableCell>Last Run</TableCell>
              <TableCell>Next Run</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pipelines.map((pipeline) => (
              <TableRow key={pipeline.pipeline_name}>
                <TableCell>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccountingIcon fontSize="small" color="primary" />
                      <Typography variant="body2" fontWeight={500}>
                        {getPipelineDisplayName(pipeline.pipeline_name)}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {getPipelineDescription(pipeline.pipeline_name)}
                    </Typography>
                  </Box>
                </TableCell>

                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="500" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ScheduleIcon fontSize="small" color="action" />
                      {cronToHumanReadable(pipeline.cron_expression || '0 2,14 * * *')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontFamily: 'monospace', fontSize: '10px' }}>
                      {pipeline.cron_expression || '0 2,14 * * *'}
                    </Typography>
                  </Box>
                </TableCell>

                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatRelativeTime(pipeline.last_run)}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {formatNextRun(pipeline.next_run)}
                  </Typography>
                </TableCell>

                <TableCell>
                  {pipeline.is_enabled ? (
                    <Chip label="Active" color="success" size="small" />
                  ) : (
                    <Chip label="Paused" color="default" size="small" />
                  )}
                </TableCell>

                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title={pipeline.is_enabled ? "Pause Pipeline" : "Resume Pipeline"}>
                      <IconButton
                        size="small"
                        onClick={() => handleTogglePipeline(
                          pipeline.pipeline_name,
                          pipeline.is_enabled
                        )}
                      >
                        {pipeline.is_enabled ? (
                          <PauseIcon fontSize="small" />
                        ) : (
                          <RunIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Run Now (1000 matches)">
                      <IconButton
                        size="small"
                        onClick={() => handleRunPipeline(pipeline.pipeline_name)}
                        color="primary"
                      >
                        <RunIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="View Job History">
                      <IconButton
                        size="small"
                        onClick={() => handleViewLogs(pipeline.pipeline_name)}
                        color="info"
                      >
                        <LogsIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Edit Schedule">
                      <IconButton
                        size="small"
                        onClick={() => handleEditPipeline(pipeline)}
                        color="secondary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Custom Run (Set Limit)">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenCustomRun(pipeline)}
                        color="secondary"
                      >
                        <ScheduleIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Pipeline Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Accounting Pipeline Settings</DialogTitle>
        <DialogContent>
          {editingPipeline && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Pipeline:</strong> {getPipelineDisplayName(editingPipeline.pipeline_name)}
              </Typography>

              <TextField
                label="Cron Expression"
                value={editForm.cron_expression}
                onChange={(e) => setEditForm({ ...editForm, cron_expression: e.target.value })}
                fullWidth
                helperText="Format: minute hour day month weekday (e.g., '0 2,14 * * *' = 2:00 AM and 2:00 PM daily)"
              />

              <TextField
                label="Sync Window (Days)"
                type="number"
                value={editForm.sync_window_days}
                onChange={(e) => setEditForm({ ...editForm, sync_window_days: parseInt(e.target.value) })}
                fullWidth
                inputProps={{ min: 1, max: 30 }}
                helperText="Number of days of historical data to look back"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom Run Dialog */}
      <Dialog open={runDialogOpen} onClose={() => setRunDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Run Accounting Pipeline with Custom Limit</DialogTitle>
        <DialogContent>
          {runningPipeline && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Pipeline:</strong> {getPipelineDisplayName(runningPipeline.pipeline_name)}
              </Typography>

              <Alert severity="info" sx={{ mt: 1 }}>
                Set the maximum number of reconciliation matches to process in this run. Processing is batched for performance.
              </Alert>

              <TextField
                label="Matches to Process"
                type="number"
                value={runLimit}
                onChange={(e) => setRunLimit(parseInt(e.target.value))}
                fullWidth
                inputProps={{ min: 10, max: 10000, step: 100 }}
                helperText="Recommended: 1000 matches per run. Max: 10,000"
              />

              <Typography variant="caption" color="text.secondary">
                The pipeline will process up to {runLimit.toLocaleString()} unprocessed matches from reconciliation tables.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRunDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCustomRun}
            variant="contained"
            color="primary"
            disabled={!runLimit || runLimit < 1}
          >
            Run Pipeline
          </Button>
        </DialogActions>
      </Dialog>

      {/* Logs Viewer Dialog */}
      <Dialog open={logsDialogOpen} onClose={() => setLogsDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Accounting Pipeline Job History</DialogTitle>
        <DialogContent>
          {loadingLogs ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedJobLogs && selectedJobLogs.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Job ID</TableCell>
                    <TableCell>Started</TableCell>
                    <TableCell>Completed</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell>Triggered By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedJobLogs.map((job) => {
                    let metadata = {};
                    try {
                      metadata = JSON.parse(job.job_metadata || '{}');
                    } catch (e) {
                      // Ignore parse errors
                    }

                    return (
                      <TableRow key={job.id}>
                        <TableCell>
                          <Typography variant="caption" fontFamily="monospace">
                            {job.id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {job.started_at ? new Date(job.started_at.replace(' ', 'T') + 'Z').toLocaleString() : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {job.completed_at ? new Date(job.completed_at.replace(' ', 'T') + 'Z').toLocaleString() : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={job.status}
                            size="small"
                            color={
                              job.status === 'completed' ? 'success' :
                              job.status === 'failed' ? 'error' :
                              job.status === 'running' ? 'primary' : 'default'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {job.duration_minutes ? `${job.duration_minutes}m` : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {job.progress_percentage}%
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={metadata.triggeredBy || 'unknown'}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              No job history found for this pipeline.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
